import { Link } from "react-router-dom";
import { matches, matchMap } from "@/mocks/matches";
import { teams } from "@/mocks/teams";
import { news } from "@/mocks/news";
import { useFavorites } from "@/hooks/useFavorites";
import MatchCard from "@/components/feature/MatchCard";
import SectionHeading from "@/components/base/SectionHeading";
import TeamLogo from "@/components/base/TeamLogo";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import SchedulePreview from "@/pages/home/components/SchedulePreview";
import NewsBrief from "@/pages/home/components/NewsBrief";
import { formatTime, formatDateLabel } from "@/utils/format";

export default function Home() {
  const { favorites } = useFavorites();

  const featured = [...matches]
    .filter((m) => m.status === "live")
    .concat(matches.filter((m) => m.status === "upcoming"))
    .slice(0, 3);

  const liveMatches = matches.filter((m) => m.status === "live");
  const upcomingMatches = matches
    .filter((m) => m.status === "upcoming")
    .sort((a, b) => a.startTime.localeCompare(b.startTime))
    .slice(0, 6);
  const topNews = news.slice(0, 3);
  const hotTeams = teams.slice(0, 6);
  const favoriteMatches = favorites.map((id) => matchMap[id]).filter(Boolean);

  return (
    <div className="px-4 md:px-8 py-6 md:py-8">
      <div className="animate-fade-up">
        <HeroCarousel matches={featured} />
      </div>

      <section className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <SectionHeading
            icon="ri-live-line"
            title="今日直播赛事"
            subtitle="正在进行的NBA比赛，点击即可进入直播详情"
            moreText="全部直播"
            moreTo="/live"
          />
          {liveMatches.length > 0 ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {liveMatches.map((match) => (
                <MatchCard key={match.id} match={match} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-12 text-center">
              <i className="ri-basketball-line text-4xl text-foreground-300" />
              <p className="mt-3 text-sm font-medium text-foreground-700">当前暂无正在直播的赛事</p>
              <p className="mt-1 text-xs text-foreground-500">比赛将在稍后开始，先看看接下来的赛程安排</p>
              <Link
                to="/schedule"
                className="mt-4 inline-flex items-center gap-1 rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 whitespace-nowrap transition-colors hover:bg-primary-600"
              >
                <i className="ri-calendar-line" /> 查看赛程
              </Link>
            </div>
          )}
        </div>

        <div>
          <SectionHeading icon="ri-newspaper-line" title="热门新闻简报" moreText="更多资讯" moreTo="/news" />
          <NewsBrief articles={topNews} />
        </div>
      </section>

      <section className="mt-10">
        <SectionHeading
          icon="ri-calendar-schedule-line"
          title="赛程预告"
          subtitle="未来几天的重点NBA对决"
          moreText="完整赛程"
          moreTo="/schedule"
        />
        <SchedulePreview matches={upcomingMatches.slice(0, 5)} />
      </section>

      <section className="mt-10">
        <SectionHeading
          icon="ri-team-line"
          title="热门球队"
          subtitle="点击球队查看阵容、战绩与相关资讯"
        />
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {hotTeams.map((team) => (
            <Link
              key={team.id}
              to={`/team/${team.id}`}
              className="flex flex-col items-center gap-2 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 hover:bg-background-100"
            >
              <TeamLogo team={team} size="md" />
              <span className="text-sm font-semibold text-foreground-900">{team.name}</span>
              <span className="text-[11px] text-foreground-500">{team.record}</span>
            </Link>
          ))}
        </div>
      </section>

      {favoriteMatches.length > 0 && (
        <section className="mt-10">
          <SectionHeading icon="ri-bookmark-3-line" title="我的收藏" subtitle="保存在本机的收藏赛事" />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {favoriteMatches.map((match) => (
              <MatchCard key={match.id} match={match} />
            ))}
          </div>
        </section>
      )}

      <section className="mt-10 rounded-lg bg-primary-900 px-6 py-8 md:px-10 md:py-10">
        <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
          <div>
            <h2 className="font-heading text-2xl font-bold text-background-50 md:text-3xl">随时掌握每一场 NBA 直播</h2>
            <p className="mt-2 max-w-2xl text-sm text-background-300">
              覆盖常规赛、季后赛的高清赛事入口，实时比分、赛程与战报一站式掌握。立即进入直播分类页，选择你支持的球队。
            </p>
          </div>
          <Link
            to="/live"
            className="inline-flex items-center gap-2 rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition-colors hover:bg-accent-600"
          >
            <i className="ri-live-line" /> 进入直播间
          </Link>
        </div>
      </section>

      <section className="mt-8 flex flex-wrap items-center gap-4 rounded-lg border border-background-200 bg-background-100 px-4 py-3 text-xs text-foreground-500">
        <span className="flex items-center gap-1">
          <i className="ri-information-line text-primary-500" /> 本站为前端演示项目，播放区域为模拟界面
        </span>
        <span className="flex items-center gap-1">
          <i className="ri-database-2-line text-accent-500" /> 全部赛事与球队数据均为虚构内容
        </span>
        <span className="flex items-center gap-1">
          <i className="ri-time-line text-secondary-600" /> 最近更新 {formatDateLabel(matches[0].startTime)} {formatTime(matches[0].startTime)}
        </span>
      </section>
    </div>
  );
}